import type { Footprint, FootprintShape, PlacedWall, Vec2 } from './types.js';

/**
 * How close two wall ends must be to count as joined.
 *
 * The plan editor snaps ends together exactly, so this only has to forgive a
 * placement typed by hand. A quarter of a brick's thickness is generous enough
 * for that and far too tight to close a gap anyone meant to leave.
 */
export const JOIN_TOLERANCE_MM = 25;

/** Right angles are what the phase 3 hip solver needs; one degree of slack. */
const RIGHT_ANGLE_TOLERANCE_DEG = 1;

interface Node {
  readonly at: Vec2;
  readonly edges: number[];
}

/**
 * The building's outline in plan.
 *
 * Taken from wall centrelines rather than a boolean union of wall rectangles.
 * A union of oriented rectangles is a robustness minefield for no benefit:
 * walls are line-like, their ends either meet or they do not, and a ring of
 * centrelines is precisely what a roof solver consumes. It also gives the plan
 * view something honest to say when the outline is open — which ends are
 * dangling — instead of a vague failure.
 */
export function footprintOf(
  walls: readonly PlacedWall[],
  toleranceMm: number = JOIN_TOLERANCE_MM,
): Footprint {
  const segments = walls
    .map((wall) => wall.centreline)
    .filter((line): line is readonly [Vec2, Vec2] => line !== null)
    // A wall shorter than the join tolerance would fold into a single node and
    // read as a self-loop, giving a node degree of two that means nothing.
    .filter((line) => Math.hypot(line[1].x - line[0].x, line[1].z - line[0].z) > toleranceMm);

  if (segments.length < 3) {
    return open(segments.flatMap((line) => [line[0], line[1]]));
  }

  // Cluster the endpoints: every end within tolerance of another is one node.
  const nodes: Node[] = [];
  const ends: number[][] = [];
  for (let i = 0; i < segments.length; i++) {
    const line = segments[i] as readonly [Vec2, Vec2];
    ends.push([nodeFor(nodes, line[0], toleranceMm, i), nodeFor(nodes, line[1], toleranceMm, i)]);
  }

  const dangling = nodes.filter((node) => node.edges.length !== 2);
  if (dangling.length > 0) return open(dangling.map((node) => node.at));

  // Every node has degree two, so the graph is a union of cycles. It is one
  // ring only if walking from any start visits every segment.
  const ring = walk(segments, ends, nodes);
  if (ring === null || ring.length !== segments.length) {
    return open([]);
  }

  const oriented = simplify(orientCounterClockwise(ring));
  if (oriented.length < 3) return open([]);
  const shape = classify(oriented);
  return {
    closed: true,
    ring: oriented,
    openEnds: [],
    shape,
    roofSupport: shape === 'irregular' ? 'gable-fallback' : 'hip-and-gable',
    areaMm2: Math.abs(signedArea(oriented)),
  };
}

function open(openEnds: readonly Vec2[]): Footprint {
  return {
    closed: false,
    ring: [],
    openEnds,
    shape: 'irregular',
    roofSupport: 'gable-fallback',
    areaMm2: 0,
  };
}

function nodeFor(nodes: Node[], at: Vec2, tolerance: number, edge: number): number {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as Node;
    if (Math.hypot(node.at.x - at.x, node.at.z - at.z) <= tolerance) {
      node.edges.push(edge);
      return i;
    }
  }
  nodes.push({ at, edges: [edge] });
  return nodes.length - 1;
}

/** Follow the ring from the first segment, returning its corner sequence. */
function walk(
  segments: readonly (readonly [Vec2, Vec2])[],
  ends: readonly number[][],
  nodes: readonly Node[],
): Vec2[] | null {
  const first = ends[0] as number[];
  const startNode = first[0] as number;
  let currentNode = first[1] as number;
  let currentEdge = 0;
  const ring: Vec2[] = [(nodes[startNode] as Node).at];
  const used = new Set<number>([0]);

  for (let guard = 0; guard < segments.length + 1; guard++) {
    if (currentNode === startNode) return ring;
    ring.push((nodes[currentNode] as Node).at);
    const node = nodes[currentNode] as Node;
    const next = node.edges.find((edge) => edge !== currentEdge && !used.has(edge));
    if (next === undefined) return null;
    used.add(next);
    const pair = ends[next] as number[];
    currentNode = (pair[0] as number) === currentNode ? (pair[1] as number) : (pair[0] as number);
    currentEdge = next;
  }
  return null;
}

/**
 * Shoelace area in the plan (x, z) plane.
 *
 * With Y up and +z running down the page, a ring that is counter-clockwise
 * seen from above has NEGATIVE area here — the plan view is a mirror of the
 * usual maths axes. Checked against a unit square turning +x toward -z.
 */
export function signedArea(ring: readonly Vec2[]): number {
  let total = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i] as Vec2;
    const b = ring[(i + 1) % ring.length] as Vec2;
    total += a.x * b.z - b.x * a.z;
  }
  return total / 2;
}

function orientCounterClockwise(ring: readonly Vec2[]): Vec2[] {
  return signedArea(ring) > 0 ? [...ring].reverse() : [...ring];
}

/**
 * Drop corners that do not turn.
 *
 * A long elevation is usually laid as several walls, so a straight side of the
 * building arrives here as a run of collinear centrelines. Those joins are not
 * corners of the footprint, and leaving them in would make a plain rectangle
 * look irregular to the roof classifier.
 */
function simplify(ring: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < ring.length; i++) {
    const previous = ring[(i + ring.length - 1) % ring.length] as Vec2;
    const at = ring[i] as Vec2;
    const next = ring[(i + 1) % ring.length] as Vec2;
    const ux = at.x - previous.x;
    const uz = at.z - previous.z;
    const vx = next.x - at.x;
    const vz = next.z - at.z;
    const lengths = Math.hypot(ux, uz) * Math.hypot(vx, vz);
    if (lengths === 0) continue;
    // Sine of the turn: collinear within a thousandth of a radian is straight.
    if (Math.abs((ux * vz - uz * vx) / lengths) > 1e-3) out.push(at);
  }
  return out;
}

/**
 * What the roof solver can do with this outline.
 *
 * `rectilinear` means every corner is a right angle, which is what an inward
 * offset skeleton needs — not that the edges are parallel to the world axes,
 * since a rotated rectangle is just as hippable as a square one.
 */
function classify(ring: readonly Vec2[]): FootprintShape {
  let rightAngles = true;
  let positive = 0;
  let negative = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i] as Vec2;
    const b = ring[(i + 1) % ring.length] as Vec2;
    const c = ring[(i + 2) % ring.length] as Vec2;
    const ux = b.x - a.x;
    const uz = b.z - a.z;
    const vx = c.x - b.x;
    const vz = c.z - b.z;
    const cross = ux * vz - uz * vx;
    const dot = ux * vx + uz * vz;
    if (cross > 0) positive += 1;
    else if (cross < 0) negative += 1;
    const turn = Math.abs((Math.atan2(cross, dot) * 180) / Math.PI);
    if (Math.abs(turn - 90) > RIGHT_ANGLE_TOLERANCE_DEG) rightAngles = false;
  }
  if (rightAngles) return 'rectilinear';
  return positive === 0 || negative === 0 ? 'convex' : 'irregular';
}
